const generation = {
    system: 'OpenAI Codex AI assistant',
    date: '13 September 2026',
    status: 'Human review pending',
    note: 'The AI synthesized the content from the listed biblical, scholarly, and historical-theological sources. Associations describe broad affinities, not formal endorsements by every person or tradition represented.',
};

export const signSeekingPerspectives = [
    {
        slug: 'is-it-right-to-ask-god-for-a-sign',
        category: 'Guidance and Discernment',
        question: 'Is it right to ask God for a sign?',
        shortTitle: 'Is it right to ask God for a sign?',
        summary: 'Scripture condemns demands for signs that test God, resist revelation, or seek forbidden knowledge, yet it also records God giving signs to strengthen faith and directing leaders through authorised means. Christians therefore range from rejecting sign-seeking for present guidance to expecting Spirit-given confirmation, with mediating views distinguished by motive, divine initiative, setting, and source.',
        introduction: 'Matthew 16 does not condemn every request for reassurance: Jesus addresses opponents who have already seen his works and ask for another sign in order to test him. Gideon asks for confirmation of a commission God has already spoken; Ahaz is rebuked for refusing a sign God offers; David consults God through the priestly ephod; and Saul, after receiving no answer through recognised channels, turns to a medium. Contemporary disagreement also reflects whether Christians believe revelatory gifts have ceased or continue under scriptural and communal discernment.',
        seoDescription: 'Compare biblical views on asking God for a sign, including Matthew 16, Gideon’s fleece, Ahaz, David’s ephod, Saul, and divination.',
        lastModified: '2026-09-13',
        keyTexts: [
            { label: 'Matthew 16:1–4', href: '/read/Matt.16/#Matt.16.1-Matt.16.4' },
            { label: 'Judges 6:36–40', href: '/read/Judg.6/#Judg.6.36-Judg.6.40' },
            { label: 'Isaiah 7:10–14', href: '/read/Isa.7/#Isa.7.10-Isa.7.14' },
            { label: '1 Samuel 23:6–12', href: '/read/1Sam.23/#1Sam.23.6-1Sam.23.12' },
            { label: '1 Samuel 28:3–20', href: '/read/1Sam.28/#1Sam.28.3-1Sam.28.20' },
        ],
        readings: [
            {
                id: 'do-not-test-established-revelation',
                title: 'Do not demand a sign after God has spoken',
                thesis: 'Asking for a sign is wrong when it puts God to the test, postpones obedience, or demands new proof while resisting revelation already given.',
                accent: 'navy',
                badges: ['Evangelical', 'Catholic', 'Reformed', 'Conservative'],
                summary: 'This reading takes Matthew 16 and Deuteronomy 6:16 as the controlling warnings. The Pharisees and Sadducees are not open inquirers lacking evidence: they test Jesus after his public teaching and works. Likewise, random “fleeces” can turn circumstances into private oracles and make obedience conditional on God meeting terms the asker has invented. Saul’s final resort to the medium at Endor shows the danger of seeking an answer at any cost when God has not answered.',
                contribution: 'It protects trust, obedience, and the sufficiency of what God has already revealed. It also resists manipulation, confirmation bias, superstition, and treating ordinary coincidences as binding divine speech.',
                caution: 'Applied without qualification, this view can make every request for reassurance sound rebellious, despite biblical cases where God offers a sign or patiently confirms a promise to a fearful servant.',
                supporters: [
                    { name: 'Craig S. Keener', sourceId: 'keener-matthew-sign' },
                    { name: 'Thomas Aquinas', sourceId: 'aquinas-tempting-god' },
                ],
                predicates: [
                    {
                        label: 'God’s established revelation should govern extraordinary claims',
                        topicSlug: 'are-miraculous-spiritual-gifts-for-today',
                        readingId: 'cessationist-apostolic-signs',
                        linked: true,
                    },
                    {
                        label: 'Silence from God does not authorise forbidden ways of obtaining knowledge',
                        linked: false,
                    },
                ],
                relations: [
                    { type: 'qualifies', target: 'confirmation-of-a-given-word', linked: true },
                    { type: 'contrasts with', target: 'authorised-inquiry-matters', linked: true },
                ],
            },
            {
                id: 'scripture-and-wisdom-not-signs',
                title: 'Do not seek signs for ordinary guidance',
                thesis: 'Christians should not ask God to guide personal decisions through signs, because Scripture, prayer for wisdom, providence, and morally responsible choice are the means God has promised.',
                accent: 'red',
                badges: ['Reformed', 'Cessationist', 'Conservative', 'Wisdom'],
                summary: 'This stricter application distinguishes biblical signs in redemptive history from a continuing method of private guidance. Gideon’s fleece belongs to an extraordinary commission and exposes hesitant faith; it does not authorise believers to make decisions by arranging coincidences. Where Scripture does not command or forbid an option, Christians should seek wisdom, counsel, and prayerfully choose without waiting for a supernatural signal.',
                contribution: 'It offers a clear present-day rule, reduces anxiety about discovering a hidden individual plan, and guards against confirmation bias or attributing divine authority to ordinary events.',
                caution: 'The rule can be stated too absolutely if it implies that God never providentially confirms a decision, answers a request unusually, or gives a non-canonical prophetic impression.',
                supporters: [
                    { name: 'Ligonier Ministries', sourceId: 'ligonier-gideon-fleece' },
                    { name: 'John MacArthur', sourceId: 'macarthur-fleece' },
                ],
                predicates: [
                    {
                        label: 'Revelatory and sign gifts belonged chiefly to the apostolic foundation',
                        topicSlug: 'are-miraculous-spiritual-gifts-for-today',
                        readingId: 'cessationist-apostolic-signs',
                        linked: true,
                    },
                    {
                        label: 'God normally guides choices through Scripture-shaped wisdom rather than private omens',
                        linked: false,
                    },
                ],
                relations: [
                    { type: 'extends', target: 'do-not-test-established-revelation', linked: true },
                    { type: 'contrasts with', target: 'expect-spirit-given-confirmation', linked: true },
                    { type: 'qualifies', target: 'confirmation-of-a-given-word', linked: true },
                ],
            },
            {
                id: 'confirmation-of-a-given-word',
                title: 'God may confirm a calling already revealed',
                thesis: 'A request for confirmation may be permissible when it seeks courage to obey a word already given rather than using a sign to discover an otherwise unknown choice.',
                accent: 'teal',
                badges: ['Evangelical', 'Reformed', 'Open but cautious'],
                summary: 'Gideon twice says that the fleece concerns what God has already said: that Israel will be saved through him. On this reading, the episode is not a general technique for choosing jobs, relationships, or other options. Gideon asks God directly to confirm an extraordinary commission already given to him, comparable to signs surrounding Moses’ calling. God’s response may display patient accommodation to weakness without making repeated sign-seeking the mature norm.',
                contribution: 'It explains why Gideon’s request receives no explicit rebuke and why the signs concern an already-spoken promise. It distinguishes reassurance in weakness from hostile testing or an attempt to manufacture guidance.',
                caution: 'Narrative permission is not necessarily a command or repeatable method. Judges 6 does not depict a community weighing a prophecy, and that later Christian practice should not be read back into Gideon’s direct encounter with God.',
                supporters: [
                    { name: 'Miles V. Van Pelt', sourceId: 'van-pelt-gideon' },
                    { name: 'John Calvin', sourceId: 'calvin-isaiah-signs' },
                ],
                predicates: [
                    {
                        label: 'Extraordinary experience may be received cautiously under prior revelation',
                        topicSlug: 'are-miraculous-spiritual-gifts-for-today',
                        readingId: 'open-but-cautious',
                        linked: true,
                    },
                    {
                        label: 'God may accommodate weakness without making weakness the model',
                        linked: false,
                    },
                ],
                relations: [
                    { type: 'qualifies', target: 'do-not-test-established-revelation', linked: true },
                    { type: 'overlaps with', target: 'receive-god-initiated-signs', linked: true },
                ],
            },
            {
                id: 'receive-god-initiated-signs',
                title: 'Receive signs that God initiates or offers',
                thesis: 'A sign is rightly received when God initiates it, commands it, or offers it for a faithful and useful purpose.',
                accent: 'amber',
                badges: ['Catholic', 'Reformed', 'Classical Christian'],
                summary: 'Isaiah 7 complicates a blanket refusal to ask for signs: God tells Ahaz to ask, and Ahaz’s pious-sounding refusal is rebuked. Scripture also presents signs given to Abraham, Moses, Gideon, and Hezekiah. Aquinas therefore distinguishes testing God’s truth or power from asking to be instructed about God’s will in a particular matter, while Calvin urges believers to receive signs God provides rather than claim an unrestricted right to devise them.',
                contribution: 'It takes seriously both sides of the canon: “do not test the Lord” and the occasions when the Lord himself gives or invites a sign. Motive and divine initiative become more decisive than the bare act of asking.',
                caution: 'People can easily redescribe a self-chosen test as God’s invitation. This view needs clear safeguards against presumption and against assigning divine authority to ambiguous events.',
                supporters: [
                    { name: 'Thomas Aquinas', sourceId: 'aquinas-tempting-god' },
                    { name: 'John Calvin', sourceId: 'calvin-isaiah-signs' },
                ],
                predicates: [
                    {
                        label: 'Present spiritual claims remain subordinate to Scripture and communal testing',
                        topicSlug: 'are-miraculous-spiritual-gifts-for-today',
                        readingId: 'regulated-continuationism',
                        linked: true,
                    },
                    {
                        label: 'Divine initiative distinguishes gift from presumption',
                        linked: false,
                    },
                ],
                relations: [
                    { type: 'qualifies', target: 'do-not-test-established-revelation', linked: true },
                    { type: 'overlaps with', target: 'confirmation-of-a-given-word', linked: true },
                ],
            },
            {
                id: 'expect-spirit-given-confirmation',
                title: 'Remain open to Spirit-given signs and confirmation',
                thesis: 'Christians may expect God to confirm guidance through prophecy, signs, impressions, and providential circumstances, provided every claim remains subordinate to Scripture and accountable discernment.',
                accent: 'teal',
                badges: ['Pentecostal', 'Charismatic', 'Continuationist', 'Missional'],
                summary: 'This approach reads Acts and the New Testament gifts as continuing patterns of Spirit-led church life. It does not treat every coincidence as a message or give a private sign the authority of Scripture. Rather, believers may pray expectantly, listen for the Spirit, receive prophetic or providential confirmation, and then test the content, purpose, messenger, and outcome within a Spirit-filled community. The Assemblies of God points to Agabus’s prophecy as confirmation of a course Paul was already prepared to follow.',
                contribution: 'It takes seriously New Testament commands not to despise prophecy and the Pentecostal testimony that signs and gifts continue to serve mission, encouragement, and discernment.',
                caution: 'Subjective impressions and ambiguous events are vulnerable to projection, social pressure, and retrospective interpretation. Without transparent testing and freedom to disagree, “confirmation” can become coercive.',
                supporters: [
                    { name: 'Assemblies of God (USA)', sourceId: 'ag-modern-prophecy' },
                ],
                predicates: [
                    {
                        label: 'The Spirit continues to distribute signs and revelatory gifts under biblical guidance',
                        topicSlug: 'are-miraculous-spiritual-gifts-for-today',
                        readingId: 'pentecostal-expectation',
                        linked: true,
                    },
                    {
                        label: 'Contemporary prophecy is contextual, accountable, and never equal to Scripture',
                        topicSlug: 'are-miraculous-spiritual-gifts-for-today',
                        readingId: 'regulated-continuationism',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'extends', target: 'receive-god-initiated-signs', linked: true },
                    { type: 'contrasts with', target: 'scripture-and-wisdom-not-signs', linked: true },
                    { type: 'qualifies', target: 'confirmation-of-a-given-word', linked: true },
                ],
            },
            {
                id: 'authorised-inquiry-matters',
                title: 'Judge inquiry by its source and authorised means',
                thesis: 'The decisive distinction is not simply supernatural inquiry versus no inquiry, but seeking YHWH through authorised covenant means rather than consulting rival powers or forbidden practitioners.',
                accent: 'red',
                badges: ['Academic', 'Historical-Critical', 'Jewish Studies'],
                summary: 'David asks questions through Abiathar and the ephod, an institution associated with priestly inquiry, and receives guidance. Saul initially seeks an answer through dreams, Urim, and prophets; his condemned turn comes when he consults the medium at Endor after YHWH does not answer. Historical study places ephod, Urim, lots, prophecy, and neighbouring divinatory practices in the same ancient world while observing that biblical texts authorise some forms and reject others.',
                contribution: 'It explains why outwardly similar attempts to obtain hidden knowledge receive different evaluations. The authority invoked, the mediator used, and fidelity to Israel’s covenant boundaries matter more than whether the procedure appears unusual to modern readers.',
                caution: 'Historical classification does not by itself determine present Christian practice. The disappearance of Israel’s priestly oracular institutions and the New Testament’s account of guidance still require theological interpretation.',
                supporters: [
                    { name: 'Jonathan Stökl', sourceId: 'stokl-ancient-divination' },
                    { name: 'Jewish Encyclopedia', sourceId: 'jewish-encyclopedia-urim' },
                ],
                predicates: [
                    {
                        label: 'Biblical discernment must distinguish authorised revelation from forbidden consultation',
                        topicSlug: 'are-miraculous-spiritual-gifts-for-today',
                        readingId: 'regulated-continuationism',
                        linked: true,
                    },
                    {
                        label: 'The source and covenant setting of an oracle are morally significant',
                        linked: false,
                    },
                ],
                relations: [
                    { type: 'qualifies', target: 'do-not-test-established-revelation', linked: true },
                    { type: 'complements', target: 'receive-god-initiated-signs', linked: true },
                ],
            },
        ],
        sources: [
            {
                id: 'keener-matthew-sign',
                author: 'Craig S. Keener',
                title: 'Jesus’ Opponents Seek a Sign (Matthew 16:1–4)',
                detail: 'The IVP New Testament Commentary distinguishes hostile testing after revelation from requests for assurance of a divine promise and reads Jesus’ resurrection as the decisive sign.',
                href: 'https://www.biblegateway.com/resources/ivp-nt/Jesus-Opponents-Seek-Sign',
            },
            {
                id: 'van-pelt-gideon',
                author: 'Miles V. Van Pelt',
                title: 'Did a Faithless Gideon Use a Fleece to Test God’s Will? (Judges 6)',
                detail: 'An ESV Expository Commentary adaptation arguing that Gideon’s fleece confirms an already-revealed commission and publicly identifies him as God’s appointed deliverer.',
                href: 'https://www.crossway.org/articles/did-a-faithless-gideon-use-a-fleece-to-test-gods-will-judges-6/',
            },
            {
                id: 'aquinas-tempting-god',
                author: 'Thomas Aquinas',
                title: 'Summa Theologiae II–II, Question 97: The Temptation of God',
                detail: 'A classical distinction between sinfully testing God’s power or truth and asking for instruction or a sign under divine command, urgency, or useful purpose.',
                href: 'https://www.newadvent.org/summa/3097.htm',
            },
            {
                id: 'calvin-isaiah-signs',
                author: 'John Calvin',
                title: 'Commentary on Isaiah 7:11',
                detail: 'Calvin permits receiving signs God offers and treats Gideon’s request as exceptional confirmation of a calling, while warning against making self-chosen signs an ordinary liberty.',
                href: 'https://ccel.org/ccel/calvin/calcom13/calcom13.xiv.i.html',
            },
            {
                id: 'stokl-ancient-divination',
                author: 'Jonathan Stökl',
                title: 'Ancient Israelite Divination: Urim ve-Tummim, Ephod, and Prophecy',
                detail: 'A historical study of priestly and prophetic inquiry in ancient Israel, including David’s use of Abiathar’s ephod and Saul’s attempts to consult YHWH.',
                href: 'https://www.thetorah.com/article/ancient-israelite-divination-urim-ve-tummim-ephod-and-prophecy',
            },
            {
                id: 'jewish-encyclopedia-urim',
                author: 'Jewish Encyclopedia',
                title: 'Urim and Thummim',
                detail: 'A reference account of priestly consultation through Urim, Thummim, and the ephod, with biblical examples involving Joshua, Saul, and David.',
                href: 'https://www.jewishencyclopedia.com/articles/14609-urim-and-thummim',
            },
            {
                id: 'ligonier-gideon-fleece',
                author: 'Ligonier Ministries',
                title: 'Gideon and the Fleece',
                detail: 'A Reformed devotional reading Gideon’s request as half-hearted faith and directing present-day decisions toward scriptural principles and wisdom rather than extrabiblical signs.',
                href: 'https://learn.ligonier.org/devotionals/gideon-and-fleece',
            },
            {
                id: 'macarthur-fleece',
                author: 'John MacArthur',
                title: 'Bible Questions and Answers, Part 74',
                detail: 'A cessationist response rejecting Gideon’s fleece as a repeatable method for present-day guidance and distinguishing it from God’s extraordinary Old Testament revelation.',
                href: 'https://www.gty.org/sermons/print/70-50/bible-questions-and-answers-part-74',
            },
            {
                id: 'ag-modern-prophecy',
                author: 'Assemblies of God (USA)',
                title: 'Modern-Day Prophecy',
                detail: 'An official Pentecostal position affirming contemporary prophecy, signs, and confirmation while requiring consistency with Scripture, accountable community judgement, and careful testing.',
                href: 'https://ag.org/Beliefs/Position-Papers/Modern-Day-Prophecy',
            },
        ],
        generation,
    },
];
